/* eslint-disable @typescript-eslint/no-use-before-define */
import { Col, Row } from 'antd';
import classnames from 'classnames';
import { config } from 'config/config';
import { isHome } from 'config/routes';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { updateChallenges } from 'src/actions/appActions';
import { updateUserPoints } from 'src/actions/authActions';
import { IResourceDetailList } from 'src/components/challenge/ChallengeInterfaces';
import { ChallengeLike } from 'src/components/challenge/ChallengeLike';
import Link from 'src/components/Link';
import { IRootReducers } from 'src/reducers';
import {
  ChallengeTypeEnum,
  ResourceTypeDetailEnum,
  ResourceTypeEnum,
} from 'src/shared/enums';
import { RoleEnum } from 'src/shared/enums/role.enum';
import {
  ChallengeDetail,
  IChallengeResource,
  IRoleType,
  TypeChallengeStatusLabel,
  TypeChallengeUserLabel,
} from 'src/shared/models';
import { absPoints, dispatchCustomEvent, slugify } from 'utils';

type ChallengeCardProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  ChallengeDetail;

const ChallengeCard: FC<ChallengeCardProps> = ({
  challengeResources,
  campaigns,
  cardImage,
  challengeCardButton,
  challengeDates,
  challengeId,
  challengeLikes,
  challengeModality,
  challengeName,
  challengeDescriptionShort,
  challengePoints,
  challengePrice,
  challengeStatusLabel,
  challengeType,
  challengeUserLabel,
  events,
  shortDateFormat,
  showPoints,
  roles = [],
  trainings,
  updateChallenges,
  updateUserPoints,
  dontShowLike,
  dontShowDates,
  challengeDescription,
  user,
}): JSX.Element => {
  const isCampaign = challengeType === ChallengeTypeEnum.CAMPAIGN;
  const isExperience = challengeType === ChallengeTypeEnum.EXPERIENCE;
  const isContest = challengeType === ChallengeTypeEnum.CONTEST;
  const isFormationCon = challengeType === ChallengeTypeEnum.FORMATION_CON;
  const isDonation = challengeType === ChallengeTypeEnum.DONATION;
  const { pathname } = useRouter();

  const { CHECKOUT_PAYMENT_GATEWAY } = ResourceTypeEnum;

  const isHomePage = isHome(pathname);

  const dateValues = {
    startDate: moment(
      challengeDates?.startDate,
      'ddd MMM DD YYYY HH:mm:ss Z',
    ).format(shortDateFormat),
    endDate: moment(
      challengeDates?.endDate,
      'ddd MMM DD YYYY HH:mm:ss Z',
    ).format(shortDateFormat),
  };

  const getChallengeTypeClass =
    ChallengeTypeEnum[challengeType].toLocaleLowerCase();

  // ? Check Challenge Lover Role
  const isLoverExperience = roles.some(
    (role: any) => role.roleType?.idRoleType === RoleEnum.LOVER,
  );

  const isLoverUser = user.roles.roles.some((userRole: IRoleType) => {
    return userRole.idRoleType.idRoleType === RoleEnum.LOVER;
  });

  //const isLoverUser = user.specialty === SpecialtyEnum.LOVER;

  const renderChallengeImage = (): JSX.Element => {
    const hasImageLink = challengeCardButton?.path;

    const image = (
      <img
        loading="lazy"
        alt={`event-${challengeId}`}
        src={cardImage ?? `${config.APP.ASSET_PREFIX}/event_image.png`}
        className={`challengeCard__image ${
          hasImageLink ? '' : 'challengeCard__image-disabled'
        }`}
      />
    );
    return image;
  };

  const renderChallengeDates = (): JSX.Element => (
    <Col className="challengeCard__date">
      <Row justify="start" align="middle">
        <a className="challengeCard__icon--small challengeCard__icon--calendar" />
        <FormattedMessage
          id="page.challenge-card.{startDate}-{endDate}"
          values={dateValues}
        />
      </Row>
    </Col>
  );

  const renderCampaignDetails = (): JSX.Element => {
    return (
      <Row>
        <Col span={12}>
          {renderCampaignDetail(
            'calendar',
            'page.challenge-card.{startDate}-{endDate}',
            dateValues,
          )}
          {renderCampaignDetail(
            'menu',
            'page.challenge-card.{actions}-actions',
            { actions: challengeResources.length },
          )}
        </Col>
        <Col className="details__container-left" span={3} />
        <Col span={9}>
          {showPoints &&
            renderCampaignDetail(
              'puntos',
              'page.challenge.plus-sign-{points}-points',
              { points: absPoints(Number(challengePoints)).toLocaleString() },
              true,
            )}
        </Col>
      </Row>
    );
  };

  const renderCampaignDetail = (
    icon: string,
    labelID: string,
    value: Record<string, any>,
    isBold?: boolean,
  ): JSX.Element => (
    <Row
      className={`details ${isBold && 'details--bold'}`}
      justify="start"
      align="middle">
      <div className={`icon icon--${icon}`} />

      <FormattedMessage id={labelID} values={value} />
    </Row>
  );

  const titleClassName = classnames('challengeCard__title', {
    'challengeCard__title--experience': isExperience,
    'challengeCard__title--formation_con': isFormationCon,
  });

  const renderChallengeName = (): JSX.Element =>
    challengeName && (
      <Row justify="start" align="middle" className={titleClassName}>
        <h4 className="challengeCard__title__text">{challengeName}</h4>
      </Row>
    );

  const renderChallengeDescription = (): JSX.Element =>
    challengeDescription && (
      <Row className="challengeCard__description">
        <p className="small-body">{challengeDescription}</p>
      </Row>
    );

  const renderChallengeDescriptionShort = (): JSX.Element =>
    challengeName && (
      <Row justify="start" align="middle">
        <p className="challengeCard__descriptionShort">
          {challengeDescriptionShort}
        </p>
      </Row>
    );

  const renderChallengeModality = (): JSX.Element => {
    return (
      <Row justify="space-between" className="challengeCard__type">
        <Col span={12}>
          <Row justify="start" className="challengeCard__type--text">
            {challengeModality && (
              <FormattedMessage
                id={`page.challenge-card.modality-${challengeModality}`}
              />
            )}
          </Row>
        </Col>
        <Col span={12}>
          {showPoints && challengePoints !== 0 && (
            <Row justify="end" className="challengeCard__points">
              <FormattedMessage
                id="menu.section.personal.{points}-point"
                values={{
                  points: absPoints(Number(challengePoints)).toLocaleString(),
                }}
              />
            </Row>
          )}
        </Col>
      </Row>
    );
  };

  const renderChallengePoints = () => {
    const isCheckoutPayment = challengeResources.some(
      (e) => e.type === CHECKOUT_PAYMENT_GATEWAY,
    );
    return (
      challengePoints !== 0 &&
      (showPoints || isHomePage) && (
        <div className="challengeCard__points">
          <i className="challengeCard__points--icon challengeCard__icon--small challengeCard__icon--points" />
          <span className="challengeCard__points--value">
            <span style={{ marginLeft: '5.4px' }}>
              {absPoints(Number(challengePoints)).toLocaleString()}
            </span>
          </span>
          <span className="challengeCard__points--text">
            <FormattedMessage id="consumable.coupon.points" />
          </span>
          {isCheckoutPayment && (
            <>
              <span className="challengeCard__points--text"> +</span>
              <span className="challengeCard__points--value">
                <span style={{ marginLeft: '5.4px' }}>
                  {absPoints(Number(challengePrice)).toLocaleString()}
                </span>
              </span>
              <span className="challengeCard__points--text">€</span>
            </>
          )}
        </div>
      )
    );
  };

  const renderContestPoints = () => {
    return (
      showPoints && (
        <>
          <div className="challengeCard__points-contest challengeCard__points-contest-label">
            <FormattedMessage
              id="menu.section.personal.{points}-point-contest"
              values={{
                points: absPoints(Number(challengePoints)).toLocaleString(),
              }}
            />
            <div className="points-contest">
              <div className="challengeCard__points--icon challengeCard__icon--small green-points" />
            </div>
          </div>
        </>
      )
    );
  };

  const renderFormationConPoints = () => {
    return (
      challengePoints !== 0 &&
      showPoints && (
        <div className="challengeCard__points">
          <i className="challengeCard__points--icon challengeCard__icon--small challengeCard__icon--points" />
          <span className="challengeCard__points--text">
            <FormattedMessage
              id="formation.challengeCard.{points}"
              values={{
                points: absPoints(Number(challengePoints)).toLocaleString(),
              }}
            />
          </span>
        </div>
      )
    );
  };

  const renderFormationConDetails = () => {
    if (!challengeResources.length) return null;

    // ? Check if the Challenge has a properly configured Certificate
    let isQuizCertificate: boolean;

    const quizCertificate = challengeResources.find(
      (challengeResource: IChallengeResource) => {
        return (
          challengeResource.resource.idResourceType.idResourceType ===
            ResourceTypeEnum.FORMATION_CERTIFICATE &&
          challengeResource.resource.status
        );
      },
    );

    if (quizCertificate) {
      isQuizCertificate = quizCertificate.resource.resourceDetailList.some(
        (resourceDetail: IResourceDetailList) => {
          return (
            resourceDetail.idResourceTypeD.idResourceTypeD ===
              ResourceTypeDetailEnum.FORMATION_CERTIFICATE_TEMPLATE &&
            resourceDetail.value !== '' &&
            resourceDetail.status
          );
        },
      );
    }

    return (
      <div>
        {isQuizCertificate && (
          <Row className="challengeCard__formation-certificate">
            <Col span={1}>
              <div className="icon icon--formation-certificate" />
            </Col>
            <Col span={23}>
              <span className="challengeCard__formation-certificate--text">
                <FormattedMessage id="formation.challengeCard.certificate" />
              </span>
            </Col>
          </Row>
        )}
      </div>
    );
  };

  const renderQrScan = (): JSX.Element => (
    <Row className="challengeCard__qr">
      <Col span={1}>
        <div className="icon icon--qr-code" />
      </Col>
      <Col span={23}>
        <span className="challengeCard__qr--text">
          <FormattedMessage id="experience.qr" />
        </span>
      </Col>
    </Row>
  );

  const handleChallengeOnClick = () => {
    isExperience &&
      dispatchCustomEvent({
        eventCategory: 'experience',
        eventAction: 'discover',
        eventLabel: undefined,
        eventName: 'experience_discover',
        eventDetail: `${slugify(`${challengeName}_${challengeType}`)}`,
      });
  };

  const renderChallengeButton = (): JSX.Element => {
    if (!challengeCardButton) return;
    return (
      <Row
        className="challengeCard__button challengeCard__button__exp"
        justify="start"
        align="middle">
        {setRenderPoints()}
      </Row>
    );
  };

  const setRenderPoints = () => {
    switch (challengeType) {
      case ChallengeTypeEnum.EXPERIENCE:
      case ChallengeTypeEnum.DONATION:
        return renderChallengePoints();
      case ChallengeTypeEnum.CONTEST:
        return renderContestPoints();
      case ChallengeTypeEnum.FORMATION_CON:
        return renderFormationConPoints();
      default:
        return (
          <div className="icon icon--arrow-dropdown-right challengeCard__button--icon" />
        );
    }
  };

  const handleUpdateChallenge = (liked: boolean) => {
    const payload = {};
    let propertyKey: string;
    let challenges: ChallengeDetail[];

    switch (challengeType) {
      case ChallengeTypeEnum.FORMATION:
        challenges = trainings;
        propertyKey = 'trainings';
        break;
      case ChallengeTypeEnum.CAMPAIGN:
        challenges = campaigns;
        propertyKey = 'campaigns';
        break;
      case ChallengeTypeEnum.TALK:
        challenges = events;
        propertyKey = 'events';
        break;
    }

    const challenge = challenges.find(
      (training) => training.challengeId == challengeId,
    );
    challenge.challengeLikes.liked = liked;

    payload[propertyKey] = [...challenges];

    updateChallenges(payload);
  };

  const showBlur =
    isExperience && challengeStatusLabel === 'coming-soon-consumer';

  const cardStatusLabel = getChallengeTypeClass + '-completed';

  return (
    <Link
      href={{
        pathname: challengeCardButton.path,
        query: { id: challengeId.toString() },
      }}>
      <a className="challengeCard__link-a" onClick={handleChallengeOnClick}>
        <Col
          id="ChallengeCard"
          className={`challengeCard challengeCard__${getChallengeTypeClass}`}>
          {showBlur && <div className="experience-carousel-card__blur" />}
          <div className="challengeCard__image">
            {renderCardStatusLabel(
              challengeUserLabel,
              challengeStatusLabel,
              cardStatusLabel,
            )}
            {renderChallengeImage()}
            {!dontShowLike && (
              <div className="challengeCard__likes">
                <Col>
                  <ChallengeLike
                    {...{
                      challengeLikes,
                      challengeId,
                      challengeType,
                      updateChallenges: handleUpdateChallenge,
                      updateUserPoints,
                      likedClassName:
                        'challengeCard__likes__heart challengeCard__likes__heart--filled',
                      dislikedClassName:
                        'challengeCard__likes__heart challengeCard__likes__heart--outlined',
                    }}
                  />
                </Col>
              </div>
            )}
          </div>
          <Row justify="start" align="top" className="challengeCard__container">
            {!isCampaign && !dontShowDates && renderChallengeDates()}
            {(isExperience || isContest || isDonation) &&
              renderChallengeDescriptionShort()}
            {renderChallengeName()}

            {!isContest &&
              !isExperience &&
              !isDonation &&
              !isCampaign &&
              !isFormationCon &&
              renderChallengeModality()}

            {isCampaign && renderCampaignDetails()}

            {(isExperience || isContest || isFormationCon || isDonation) &&
              renderChallengeDescription()}

            {isFormationCon && renderFormationConDetails()}

            {isExperience &&
              isLoverExperience &&
              !isLoverUser &&
              renderQrScan()}
            {renderChallengeButton()}
          </Row>
        </Col>
      </a>
    </Link>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ updateUserPoints, updateChallenges }, dispatch);

const mapStateToProps = (state: IRootReducers) => {
  return {
    ...state.app.challenges,
    user: state.auth.user,
    shortDateFormat: state.platform.shortDateFormat,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ChallengeCard);

export const renderCardStatusLabel = (
  challengeUserLabel: TypeChallengeUserLabel,
  challengeStatusLabel: TypeChallengeStatusLabel,
  labelCompleted?: string,
) => {
  const status =
    challengeUserLabel === labelCompleted
      ? challengeUserLabel
      : challengeStatusLabel;
  return (
    status && (
      <label
        className={`challengeCard__image__label challengeCard__image__label--${status}`}>
        <FormattedMessage id={`page.challenge-card.status-${status}`} />
      </label>
    )
  );
};
